import {
  createManagementDashboardApi,
  type ManagementDashboardAnalyticsKpisResponse,
} from '@e-learning/core';
import type {
  AdminAnalyticsKpiCardVariant,
  AdminAnalyticsKpiTrendTone,
} from '@e-learning/shared';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../../../api/client';

const managementDashboardApi = createManagementDashboardApi(api);

const NS = 'common';
const PREFIX = 'admin_dashboard.analytics';

function formatSignedPercentInt(n: number): string {
  const rounded = Math.round(n);
  if (rounded > 0) return `+${rounded}`;
  if (rounded < 0) return String(rounded);
  return '0';
}

function formatSignedPercentOneDecimal(n: number): string {
  const rounded = Math.round(n * 10) / 10;
  const s = rounded.toFixed(1);
  if (rounded > 0) return `+${s}`;
  if (rounded < 0) return s;
  return '0';
}

function formatPercentDisplay(n: number, fractionDigits: number): string {
  return `${n.toFixed(fractionDigits)}%`;
}

export interface AdminDashboardAnalyticsKpiCardModel {
  variant: AdminAnalyticsKpiCardVariant;
  labelKey: string;
  value: string;
  trend: string;
  trendTone: AdminAnalyticsKpiTrendTone;
}

function mapResponseToCards(
  d: ManagementDashboardAnalyticsKpisResponse,
  t: (key: string, opt?: Record<string, string | number>) => string
): AdminDashboardAnalyticsKpiCardModel[] {
  const trendPrefix = `${PREFIX}.trend`;

  const usersGrowth = d.usersKpi.activeUsersGrowthPercent;
  let usersTrend: string;
  let usersTone: AdminAnalyticsKpiTrendTone;
  if (usersGrowth === 0) {
    usersTrend = t(`${trendPrefix}.users_no_change`);
    usersTone = 'muted';
  } else {
    usersTrend = t(`${trendPrefix}.users_this_month`, {
      pct: formatSignedPercentInt(usersGrowth),
    });
    usersTone = usersGrowth > 0 ? 'positive' : 'negative';
  }

  const courseDelta = d.activeCoursesKpi.activeCoursesDeltaVsPreviousMonth;
  let coursesTrend: string;
  let coursesTone: AdminAnalyticsKpiTrendTone;
  if (courseDelta === 0) {
    coursesTrend = t(`${trendPrefix}.courses_no_change`);
    coursesTone = 'muted';
  } else if (courseDelta > 0) {
    coursesTrend = t(`${trendPrefix}.courses_new`, { count: courseDelta });
    coursesTone = 'positive';
  } else {
    coursesTrend = t(`${trendPrefix}.courses_fewer`, {
      count: Math.abs(courseDelta),
    });
    coursesTone = 'negative';
  }

  const cr = d.completionRateKpi.completionRatePercent;
  const crDelta = d.completionRateKpi.completionRateDeltaVsPreviousMonth;
  let completionValue: string;
  let completionTrend: string;
  let completionTone: AdminAnalyticsKpiTrendTone;

  if (cr === null || Number.isNaN(Number(cr))) {
    completionValue = t(`${PREFIX}.value_not_available`);
  } else {
    completionValue = formatPercentDisplay(Math.round(Number(cr) * 10) / 10, 1);
  }

  if (crDelta === null || Number.isNaN(Number(crDelta))) {
    completionTrend = t(`${trendPrefix}.completion_no_compare`);
    completionTone = 'muted';
  } else if (crDelta === 0) {
    completionTrend = t(`${trendPrefix}.completion_no_compare`);
    completionTone = 'muted';
  } else {
    completionTrend = t(`${trendPrefix}.completion_vs_month`, {
      pct: formatSignedPercentOneDecimal(Number(crDelta)),
    });
    completionTone = crDelta > 0 ? 'positive' : 'negative';
  }

  const avg = d.assessmentScoreKpi.averageAssessmentScorePercent;
  const asDelta = d.assessmentScoreKpi.assessmentScoreDeltaVsPreviousMonth;
  const assessmentValue = formatPercentDisplay(
    Math.round(avg * 100) / 100,
    2
  );

  let assessmentTrend: string;
  let assessmentTone: AdminAnalyticsKpiTrendTone;
  if (asDelta === 0) {
    assessmentTrend = t(`${trendPrefix}.assessment_no_change`);
    assessmentTone = 'muted';
  } else {
    assessmentTrend = t(`${trendPrefix}.assessment_vs_month`, {
      pct: formatSignedPercentOneDecimal(asDelta),
    });
    assessmentTone = asDelta > 0 ? 'positive' : 'negative';
  }

  return [
    {
      variant: 'blue',
      labelKey: `${PREFIX}.cards.total_users`,
      value: String(d.usersKpi.totalActiveUsers),
      trend: usersTrend,
      trendTone: usersTone,
    },
    {
      variant: 'purple',
      labelKey: `${PREFIX}.cards.active_courses`,
      value: String(d.activeCoursesKpi.totalActiveCourses),
      trend: coursesTrend,
      trendTone: coursesTone,
    },
    {
      variant: 'green',
      labelKey: `${PREFIX}.cards.completion_rate`,
      value: completionValue,
      trend: completionTrend,
      trendTone: completionTone,
    },
    {
      variant: 'yellow',
      labelKey: `${PREFIX}.cards.avg_assessment_score`,
      value: assessmentValue,
      trend: assessmentTrend,
      trendTone: assessmentTone,
    },
  ];
}

export function useAdminDashboardAnalyticsKpis() {
  const { t } = useTranslation(NS);
  const [data, setData] =
    useState<ManagementDashboardAnalyticsKpisResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(false);
    managementDashboardApi
      .fetchAnalyticsKpis()
      .then(res => {
        if (!cancelled) setData(res);
      })
      .catch(() => {
        if (!cancelled) {
          setData(null);
          setError(true);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [reloadToken]);

  const refetch = useCallback(() => {
    setReloadToken(x => x + 1);
  }, []);

  const cards = useMemo(() => {
    if (!data) return [];
    return mapResponseToCards(data, t);
  }, [data, t]);

  return {
    loading,
    error,
    cards,
    refetch,
  };
}
